import React, { useState, useEffect, useRef } from 'react';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Box, Container, Typography, Card, CardContent, IconButton, useTheme, useMediaQuery, Button, Snackbar, Alert, Divider, LinearProgress } from '@mui/material';
import { Assessment, TrackChanges, SupervisedUserCircle, AccountBalanceWallet, Storefront, Devices, ExpandLess, ExpandMore, Close, RestartAlt } from '@mui/icons-material';
import { restaurantActiveSaleApi, restaurantCompletedSaleApi } from '../../services/restaurantApiService';
import { dashboardLayoutService, DashboardLayoutData, LayoutItem } from '../../services/dashboardLayoutService';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';

const RESTAURANT_SCREEN_ID = 1;
const SCREEN_TYPE = 'QuickStart';
const USER_ID = 1;
const COMPANY_ID = 1;

// Kart içerikleri için tip tanımı
interface CardContentData {
  label: string;
  value: string | number;
}

// Her kart için içerik render fonksiyonu
const renderCardContent = (cardKey: string, reportStats: any) => {
  const contentMap: { [key: string]: CardContentData[] } = {
    'Raporlar': [
      { label: 'Toplam Satış', value: reportStats ? `₺${reportStats.totalRevenue.toLocaleString('tr-TR')}` : '₺0' },
      { label: 'Tamamlanan Sipariş', value: reportStats?.completedCount || 0 },
      { label: 'Aktif Sipariş', value: reportStats?.activeCount || 0 },
      { label: 'İptal Edilen Satış', value: reportStats ? `₺${reportStats.cancelledTotal.toLocaleString('tr-TR')}` : '₺0' },
      { label: 'Ortalama Sipariş Tutarı', value: '₺248' },
      { label: 'Günlük Hedef', value: '₺15.000' },
      { label: 'Aylık Toplam', value: '₺425.680' },
      { label: 'En Çok Satan Ürün', value: 'Pizza Margherita' },
    ],
    'Görev Takip': [
      { label: 'Bekleyen Görevler', value: 8 },
      { label: 'Devam Eden', value: 15 },
      { label: 'Tamamlanan', value: 42 },
      { label: 'Geciken', value: 3 },
      { label: 'Bugün Eklenen', value: 5 },
      { label: 'Yüksek Öncelikli', value: 12 },
      { label: 'Orta Öncelikli', value: 8 },
      { label: 'Düşük Öncelikli', value: 3 },
    ],
    'İnsan Kaynakları': [
      { label: 'Toplam Çalışan', value: 42 },
      { label: 'Aktif Personel', value: 38 },
      { label: 'İzinli', value: 3 },
      { label: 'Rapor Bekleyen', value: 1 },
      { label: 'Tam Zamanlı', value: 35 },
      { label: 'Yarı Zamanlı', value: 7 },
      { label: 'Bugün Mesaiye Gelen', value: 28 },
      { label: 'Ortalama Çalışma Saati', value: '8.5 saat' },
    ],
    'Ön Muhasebe': [
      { label: 'Toplam Gelir', value: '₺145.250' },
      { label: 'Toplam Gider', value: '₺68.420' },
      { label: 'Net Kar', value: '₺76.830' },
      { label: 'Bekleyen Ödemeler', value: '₺12.500' },
      { label: 'Nakit Akışı', value: '₺52.340' },
      { label: 'Banka Bakiyesi', value: '₺185.670' },
      { label: 'Ödenmemiş Faturalar', value: '₺8.900' },
      { label: 'Tahsilat Bekleyen', value: '₺15.200' },
    ],
    'Satış': [
      { label: 'Bugünkü Satış', value: reportStats ? `₺${reportStats.activeTotal.toLocaleString('tr-TR')}` : '₺0' },
      { label: 'Dünkü Satış', value: '₺12.850' },
      { label: 'Haftalık Satış', value: '₺89.450' },
      { label: 'Aylık Satış', value: '₺425.680' },
      { label: 'Ortalama Sepet', value: '₺248' },
      { label: 'Toplam Müşteri', value: 1847 },
      { label: 'Yeni Müşteri', value: 156 },
      { label: 'Tekrar Eden Müşteri', value: 1691 },
    ],
    'Cihaz Yönetimi': [
      { label: 'Aktif Cihazlar', value: 12 },
      { label: 'Çevrimdışı', value: 2 },
      { label: 'Bakım Gereken', value: 1 },
      { label: 'Toplam Cihaz', value: 15 },
      { label: 'Yazıcılar', value: 5 },
      { label: 'POS Cihazları', value: 8 },
      { label: 'Tablet', value: 2 },
      { label: 'Son Bakım Tarihi', value: '15 Ara 2025' },
    ],
    'Memnuniyet': [
      { label: 'Ortalama Puan', value: '4.6/5' },
      { label: 'Toplam Değerlendirme', value: 1248 },
      { label: 'Bu Ay', value: 156 },
      { label: 'Bu Hafta', value: 38 },
      { label: '5 Yıldız', value: 892 },
      { label: '4 Yıldız', value: 256 },
      { label: '3 Yıldız', value: 78 },
      { label: '2 Yıldız ve Altı', value: 22 },
    ],
  };

  const content = contentMap[cardKey] || [];

  return (
    <Box sx={{ mt: 2 }}>
      {content.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && <Divider sx={{ my: 1.5 }} />}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {item.label}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {item.value}
            </Typography>
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
};

const allCards = [
  { key: 'Raporlar', title: 'Raporlar', icon: <Assessment fontSize="large" />, color: '#673ab7', w: 4, h: 3 },
  { key: 'Görev Takip', title: 'Görev Takip', icon: <TrackChanges fontSize="large" />, color: '#1976d2', w: 4, h: 3 },
  { key: 'İnsan Kaynakları', title: 'İnsan Kaynakları', icon: <SupervisedUserCircle fontSize="large" />, color: '#9c27b0', w: 4, h: 3 },
  { key: 'Ön Muhasebe', title: 'Ön Muhasebe', icon: <AccountBalanceWallet fontSize="large" />, color: '#f57c00', w: 4, h: 3 },
  { key: 'Satış', title: 'Satış', icon: <Storefront fontSize="large" />, color: '#388e3c', w: 4, h: 3 },
  { key: 'Cihaz Yönetimi', title: 'Cihaz Yönetimi', icon: <Devices fontSize="large" />, color: '#0097a7', w: 4, h: 3 },
  { key: 'Memnuniyet', title: 'Memnuniyet', icon: <Assessment fontSize="large" />, color: '#43a047', w: 4, h: 3 },
];

const initialSideInfoCards = [
  { key: 'personel', label: 'Toplam Personel', value: 42, icon: <PeopleAltIcon color="primary" /> },
  { key: 'pendingTasks', label: 'Bekleyen Görev', value: 8, icon: <AssignmentLateIcon color="warning" /> },
  { key: 'todaySales', label: 'Bugünkü Satış', value: '₺2.300', icon: <MonetizationOnIcon color="success" /> },
  { key: 'info', label: 'Duyuru', value: 'Yeni güncelleme yayında!', icon: <InfoIcon color="info" /> },
];

const QuickStartPage: React.FC = () => {
  const [cards, setCards] = useState(allCards);
  const [sideCards, setSideCards] = useState(initialSideInfoCards);
  const [collapsed, setCollapsed] = useState<{ [key: string]: boolean }>({});
  const [dragging, setDragging] = useState(false);
  const [pendingLayout, setPendingLayout] = useState<LayoutItem[]>([]);
  const [sidePendingLayout, setSidePendingLayout] = useState<LayoutItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  const [reportStats, setReportStats] = useState<null | {
    activeCount: number;
    activeTotal: number;
    completedCount: number;
    cancelledTotal: number;
    totalRevenue: number;
    loading: boolean;
    error?: string;
  }>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(1200);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  let gridTemplateColumns = '1fr 240px';
  if (isSmDown) {
    gridTemplateColumns = '1fr';
  } else if (isMdDown) {
    gridTemplateColumns = '1fr';
  }

  const generateDefaultLayout = () => {
    return allCards.map((card, i) => ({
      i: card.key,
      x: (i * 4) % 12,
      y: Math.floor(i / 3) * 3,
      w: card.w,
      h: card.h,
      minW: 3,
      maxW: 6,
      minH: 2,
      maxH: 6,
    }));
  };

  const generateDefaultSideLayout = () => {
    return initialSideInfoCards.map((card, i) => ({
      i: card.key,
      x: 0,
      y: i,
      w: 1,
      h: 1,
    }));
  };

  useEffect(() => {
    loadLayoutFromDatabase();
  }, []);

  const loadLayoutFromDatabase = async () => {
    try {
      setLoading(true);
      const savedLayout = await dashboardLayoutService.getLayout(USER_ID, SCREEN_TYPE);

      if (savedLayout && savedLayout.mainLayout && savedLayout.cardOrder) {
        const orderedCards = savedLayout.cardOrder
          .map((key: string) => allCards.find(c => c.key === key))
          .filter(Boolean) as typeof allCards;
        
        setCards(orderedCards);
        setPendingLayout(savedLayout.mainLayout);

        if (savedLayout.sideLayout && savedLayout.sideCardOrder) {
          const orderedSideCards = savedLayout.sideCardOrder
            .map((key: string) => initialSideInfoCards.find(c => c.key === key))
            .filter(Boolean) as typeof initialSideInfoCards;
          
          setSideCards(orderedSideCards);
          setSidePendingLayout(savedLayout.sideLayout);
        } else {
          setSidePendingLayout(generateDefaultSideLayout());
        }
      } else {
        setPendingLayout(generateDefaultLayout());
        setSidePendingLayout(generateDefaultSideLayout());
      }
    } catch (error) {
      console.error('Layout yükleme hatası:', error);
      setPendingLayout(generateDefaultLayout());
      setSidePendingLayout(generateDefaultSideLayout());
      setSnackbar({ open: true, message: 'Layout yüklenemedi, varsayılan kullanılıyor', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && pendingLayout.length > 0) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      saveTimeoutRef.current = setTimeout(() => {
        saveLayoutToDatabase();
      }, 2000);
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [pendingLayout, cards, sidePendingLayout, sideCards, loading]);

  const saveLayoutToDatabase = async () => {
    try {
      const layoutData: DashboardLayoutData = {
        mainLayout: pendingLayout,
        sideLayout: sidePendingLayout,
        cardOrder: cards.map(c => c.key),
        sideCardOrder: sideCards.map(c => c.key),
      };

      const success = await dashboardLayoutService.saveLayout({
        userId: USER_ID,
        screenType: SCREEN_TYPE,
        layout: layoutData,
      });

      if (success) {
        setSnackbar({ open: true, message: 'Tasarım kaydedildi', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Kaydetme başarısız', severity: 'error' });
      }
    } catch (error) {
      console.error('Layout kaydetme hatası:', error);
      setSnackbar({ open: true, message: 'Kaydetme hatası', severity: 'error' });
    }
  };

  const handleResetLayout = async () => {
    if (window.confirm('Tüm tasarım ayarları sıfırlanacak. Emin misiniz?')) {
      const success = await dashboardLayoutService.resetLayout(USER_ID, SCREEN_TYPE);
      if (success) {
        setCards(allCards);
        setSideCards(initialSideInfoCards);
        setPendingLayout(generateDefaultLayout());
        setSidePendingLayout(generateDefaultSideLayout());
        setSnackbar({ open: true, message: 'Tasarım sıfırlandı', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Sıfırlama başarısız', severity: 'error' });
      }
    }
  };

  useEffect(() => {
    let mounted = true;
    async function fetchReportStats() {
      setReportStats({
        activeCount: 0,
        activeTotal: 0,
        completedCount: 0,
        cancelledTotal: 0,
        totalRevenue: 0,
        loading: true,
      });
      try {
        const activeSales = await restaurantActiveSaleApi.getByRestaurantScreenId(RESTAURANT_SCREEN_ID);
        const completedSales = await restaurantCompletedSaleApi.getByRestaurantScreenId(RESTAURANT_SCREEN_ID);
        const cancelled = completedSales.filter(sale => sale.Notes && sale.Notes.toLowerCase().includes('iptal'));
        const totalRevenue = completedSales.reduce((sum, sale) => sum + (sale.TotalAmount || 0), 0);
        const activeTotal = activeSales.reduce((sum, sale) => sum + (sale.TotalAmount || 0), 0);
        if (mounted) {
          setReportStats({
            activeCount: activeSales.length,
            activeTotal,
            completedCount: completedSales.length,
            cancelledTotal: cancelled.reduce((sum, sale) => sum + (sale.TotalAmount || 0), 0),
            totalRevenue,
            loading: false,
          });
        }
      } catch (error: any) {
        if (mounted) {
          setReportStats({
            activeCount: 0,
            activeTotal: 0,
            completedCount: 0,
            cancelledTotal: 0,
            totalRevenue: 0,
            loading: false,
            error: error.message || 'Veri alınamadı',
          });
        }
      }
    }
    fetchReportStats();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const handleResize = () => {
      if (containerRef.current) {
        setGridWidth(containerRef.current.offsetWidth);
      }
    };
    handleResize();
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(containerRef.current);
    window.addEventListener('resize', handleResize);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleRemove = (key: string) => setCards(cards => cards.filter(c => c.key !== key));
  const handleDragStart = () => setDragging(true);
  
  const handleDragStop = (layoutAfterDrop: any[], oldItem: any, newItem: any) => {
    setDragging(false);
    const overlapped = layoutAfterDrop.filter((l: any) => 
      l.i !== newItem.i &&
      l.x < newItem.x + newItem.w &&
      l.x + l.w > newItem.x &&
      l.y < newItem.y + newItem.h &&
      l.y + l.h > newItem.y
    );
    
    if (overlapped.length > 0) {
      const swapItem = overlapped[0];
      const newLayout = layoutAfterDrop.map((l: any) => {
        if (l.i === newItem.i) return { ...l, x: swapItem.x, y: swapItem.y };
        if (l.i === swapItem.i) return { ...l, x: oldItem.x, y: oldItem.y };
        return l;
      });
      setPendingLayout(newLayout);
    } else {
      setPendingLayout(layoutAfterDrop);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Yükleniyor...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ p: 0 }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<RestartAlt />}
          onClick={handleResetLayout}
          size="small"
        >
          Tasarımı Sıfırla
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns, gap: 0, width: '100%', minHeight: '800px', background: '#f8f8f8' }}>
        <Box ref={containerRef} sx={{ py: 0, px: 2, minHeight: '800px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <GridLayout
            className="layout"
            layout={pendingLayout}
            cols={isSmDown ? 1 : isMdDown ? 2 : 12}
            rowHeight={isSmDown ? 180 : 120}
            width={containerRef.current ? containerRef.current.offsetWidth : 1200}
            margin={[8, 8]}
            draggableHandle=".card-drag-handle"
            isResizable={true}
            isDraggable={true}
            useCSSTransforms={true}
            compactType={'vertical'}
            preventCollision={false}
            isBounded={true}
            allowOverlap={dragging}
            onDragStart={handleDragStart}
            onDragStop={handleDragStop}
            onLayoutChange={(newLayout) => setPendingLayout(newLayout)}
            style={{ minHeight: '800px', background: '#f8f8f8' }}
          >
            {cards.map(card => (
              <div key={card.key}>
                <Card sx={{ 
                  height: '100%', 
                  minHeight: collapsed[card.key] ? 56 : 200, 
                  display: 'flex', 
                  flexDirection: 'column',
                  overflow: 'auto'
                }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className="card-drag-handle" style={{ cursor: 'move', fontWeight: 'bold', color: card.color, display: 'flex', alignItems: 'center' }}>
                        {card.icon}
                        <span style={{ marginLeft: 8 }}>{card.title}</span>
                      </span>
                      <Box>
                        <IconButton size="small" onClick={() => setCollapsed(c => ({ ...c, [card.key]: !c[card.key] }))}>
                          {collapsed[card.key] ? <ExpandMore /> : <ExpandLess />}
                        </IconButton>
                        <IconButton size="small" onClick={() => handleRemove(card.key)}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    </div>
                    {!collapsed[card.key] && (
                      reportStats?.loading ? (
                        <Box sx={{ mt: 2 }}>
                          <LinearProgress />
                        </Box>
                      ) : (
                        renderCardContent(card.key, reportStats)
                      )
                    )}
                  </CardContent>
                </Card>
              </div>
            ))}
          </GridLayout>
        </Box>

        {gridTemplateColumns !== '1fr' && (
          <Box sx={{ px: 2, py: 4, width: 220, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <GridLayout
              className="side-info-layout"
              layout={sidePendingLayout}
              cols={1}
              rowHeight={64}
              width={200}
              isResizable={false}
              isDraggable={true}
              useCSSTransforms={true}
              margin={[8, 8]}
              onLayoutChange={(newLayout) => {
                setSidePendingLayout(newLayout);
                const newOrder = newLayout.map(l => sideCards.find(c => c.key === l.i)).filter(Boolean);
                setSideCards(newOrder as typeof sideCards);
              }}
            >
              {sideCards.map(card => (
                <div key={card.key}>
                  <Card sx={{ p: 1, display: 'flex', alignItems: 'center', minHeight: 56, boxShadow: 2 }}>
                    <Box sx={{ mr: 2 }}>{card.icon}</Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{card.label}</Typography>
                      <Typography variant="subtitle2" color="text.secondary">{card.value}</Typography>
                    </Box>
                  </Card>
                </div>
              ))}
            </GridLayout>
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default QuickStartPage;